import Diff from 'edge/platform/diff/Diff';
import _ from 'lodash';


export default ['StateUtils', (StateUtils) => {
    const submissionStatuses = {};
    const submissionSnapshots = {};
    let refreshFn;

    /**
    * Function to check if the submission has differences from the snapshot
    *
    * @param {number} quoteId - the quoteId of the submission
    * @param {Object} submission - the current submission data to compare to the snapshot
    * @returns {Boolean}
    */
    function _checkHasDiff(quoteId, submission) {
        const snapshot = (submissionSnapshots[quoteId] && submissionSnapshots[quoteId].submission) || {};
        const diff = Diff.diffDeep(submission, snapshot, ['tempId', '$$hashKey', 'branch', 'periodStatus', 'status', 'displayAddress', 'addressType', 'lineCoverageArray', 'schedulesArray', 'additionalCoverageArray']);
        return diff.length !== 0;
    }

    /**
    * Function to get the submission data from the state
    *
    * @returns {Object} - the current submission data
    */
    function _getSubmissionFromState() {
        const model = StateUtils.activeDataForState(true).model;
        return (model && model.value) || {};
    }

    /**
    * Function to get the index of the step in the wizard flow
    *
    * @param {string} stepName - the name of the step
    * @param {Object} flowModel - the flowModel if not supplied get it from the state
    *
    * @returns {number}- the index of the step
    */
    function _getStepIndex(stepName, flowModel = StateUtils.activeDataForState(true).flowModel) {
        return !flowModel
            ? -1
            : _.findIndex(flowModel.steps, {
                name: stepName
            });
    }

    return {
        /**
        * Function to set the snapshot and highest index
        * if no status added or moving past the furthest completed step set the snapshot set the highest status
        * if moving forward in the flow set the snapshot submission to the current submission data and new quote = false
        *
        * @param {Object} submission - the current submission data to compare to the snapshot
        * @param {Object} currentStep - the step just left
        * @param {Object} targetStep - the step moving to
        */
        addSnapshotAndHighestIndex: (submission, currentStep, targetStep) => {
            const currentStepIndex = _getStepIndex(currentStep.name);
            const targetStepIndex = _getStepIndex(targetStep.name);
            const quoteId = submission ? submission.quoteID : _getSubmissionFromState().quoteID;
            if (!quoteId) {
                return;
            }
            if (!submissionStatuses[quoteId] || currentStepIndex > submissionStatuses[quoteId]) {
                submissionStatuses[quoteId] = currentStepIndex;
            }
            if (currentStepIndex > targetStepIndex) {
                const currentSubmission = _.cloneDeep(submission);
                const isNewQuote = submissionSnapshots[quoteId] && submissionSnapshots[quoteId].isNewQuote;
                submissionSnapshots[quoteId] = {submission: currentSubmission, isNewQuote: typeof isNewQuote === 'undefined' ? undefined : false };
            }
        },
        /**
        * Function to set the snapshot on first quote
        * if no snapshot added add submission to snapshot and new quote = true
        * should only set snapshot first time goes to quote page
        *
        * @param {Object} submission - the current submission data to add to the snapshot
        */
        addQuoteSnapshot(submission) {
            const quoteId = submission ? submission.quoteID : _getSubmissionFromState().quoteID;
            // is it undefined ? set submission and changed = true to ensure it generates quote
            if (!submissionSnapshots[quoteId] || typeof submissionSnapshots[quoteId].isNewQuote === 'undefined') {
                submissionSnapshots[quoteId] = {submission, isNewQuote: true};
            }
        },

        /**
        * Function to calculate whether submission needs to be quoted
        * if no snapshot exist submission needs to be generate quote
        * is current submission does not match snapshot and it is not a new quote submission needs to be generate quote
        * @param {Object} submission - the current submission data to compare to the snapshot
        *
        * @returns {Boolean}
        */
        shouldSkipQuote: (submission) => {
            if (!submission) { // PSA: addon for case, when validation error is returned from LoadSave and submission is not available
                return false;
            }
            const quoteId = submission.quoteID || _getSubmissionFromState().quoteID;
            if (!submissionSnapshots[quoteId]) {
                return false;
            }
            const {issues, fieldIssues} = _.get(submission, 'errorsAndWarnings.validationIssues', {});
            if (!_.isEmpty(issues) || !_.isEmpty(fieldIssues)) {
                // we always want to requote/re-validate if we have validation issues
                return false;
            }
            const isChanged = _checkHasDiff(quoteId, submission);
            return !isChanged && !submissionSnapshots[quoteId].isNewQuote;
        },

        /**
        * Function to calculate whether a step has been visited
        * if current submission does not match snapshot and it is not a new quote submission the step has to be before the currentStep
        * Otherwise if the submission matches the snapshot if the highest step reached is greater than the step
        * @param {number} stepIndex - the index of the step in the flow
        * @param {number} currentStepIndex - the index of the currentStep in the flow
        *
        * @returns {Boolean}-  has stepIndex been visited
        */
        isStepIndexVisited(stepIndex, currentStepIndex) {
            const submission = _getSubmissionFromState();
            const quoteId = submission.quoteID;
            const isNewQuote = !!(submissionSnapshots[quoteId] && submissionSnapshots[quoteId].isNewQuote);
            const isChanged = _checkHasDiff(quoteId, submission);
            if (isChanged && !isNewQuote) {
                return stepIndex <= currentStepIndex;
            }
            return submissionStatuses[quoteId] >= stepIndex;
        },

        /**
        * Function to check if the should allow move from currentStep to targetStep
        * if current submission does not match snapshot reset the highest index an donly allow to move to a step before the current
        * Otherwise allow to move to steps before the furthest step in the flow
        *
        * @param {Object} flowModel - the flowModel
        * @param {Object} currentStep - the step just left
        * @param {Object} targetStep - the step moving to
        *
        * @returns {Boolean}
        */
        checkIfTransitionAllowed(flowModel, currentStep, targetStep) {
            const submission = _getSubmissionFromState();
            const quoteId = submission.quoteID;
            const currentStepIndex = _getStepIndex(currentStep.name, flowModel);
            const targetStepIndex = _getStepIndex(targetStep.name || targetStep, flowModel);

            // PSA - don't only allow going back in the wizard in case Confirmation is reached
            if (currentStep.name === 'puwConfirmation') {
                return currentStepIndex < targetStepIndex;
            }

            const isChanged = _checkHasDiff(quoteId, submission);
            if (isChanged) {
                submissionStatuses[quoteId] = currentStepIndex;
                if (refreshFn) { // refresh the sidebar
                    refreshFn();
                }
                return targetStepIndex < currentStepIndex;
            }
            return submissionStatuses[quoteId] >= targetStepIndex;
        },
        /**
        * exposing private variable for testing purposes
        *
        * @param {number | string} quoteId - the quoteId
        *
        * @returns {number} - the highest visited index in the flow
        */
        getSubmissionStatus(quoteId) {
            return submissionStatuses[quoteId];
        },
        /**
        * exposing private variable for testing purposes
        *
        * @param {number | string} quoteId - the quoteId
        *
        * @returns {Object} - the snapshot object
        */
        getSubmissionSnapshot(quoteId) {
            return submissionSnapshots[quoteId];
        },

        /**
        * Utility function to set a callback function that can be called by this service
        * used to refresh the wizard sidebar when executing checkIfTransitionAllowed
        *
        * @param {function} fn - the callback function
        */
        setRefreshFn(fn) {
            refreshFn = fn;
        }
    };
}];